"use client";

import {
    useState,
    ReactNode,
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
} from "react";
import InteractionModal from "../interactionModal/interactionModal";
import { GetVideoResponseType } from "@/features/storage/sheets/hooks/useGetVideo";
import { useGetInteractions } from "@/features/storage/sheets/hooks/useGetInteractions";
import { LoaderCircle } from "lucide-react";
import { useInteractionModalSteps } from "./useInteractionModalSteps";
import { useSaveInteractions } from "./useSaveInteractions";
import { useInteractionEdit } from "./useInteractionEdit";
import { useInteractionsList } from "./useInteractionsList";
import { interactionModalSteps } from "../lib/utils";
import { SrclangType, VideoInteraction } from "@yedalabs/schema";
import {
    InteractionsContext,
    InteractionsContextType,
    useInteractions as sharedUseInteractions,
} from "@/features/videoInteractions/context";
import TimeAndTypeStep from "../interactionModal/steps/timeAndTypeStep";

interface InteractionsProviderProps {
    children: ReactNode;
    video: GetVideoResponseType["data"];
    setReloadVideoKey: Dispatch<SetStateAction<number>>;
}

// Re-export so existing imports from this file keep working.
export const useInteractions = sharedUseInteractions;
export { InteractionsContext };

export default function InteractionsProvider({
    children,
    video,
    setReloadVideoKey,
}: InteractionsProviderProps): JSX.Element {
    const [interactions, setInteractions] = useState<VideoInteraction[]>([]);
    const [selectedInteraction, setSelectedInteraction] =
        useState<VideoInteraction | null>(null);
    const [interactionModalOpen, setInteractionModalOpen] = useState(false);

    const { data, isPending } = useGetInteractions(video.id);

    const spokenSrclang = video.captions.find(
        (c) => c.isSpokenLanguage
    )?.srclang;

    // Interactions are stored one set per caption language. The tab views/edits
    // a single language at a time, defaulting to the spoken language.
    const [selectedSrclang, setSelectedSrclang] = useState<
        SrclangType | undefined
    >(spokenSrclang);

    useEffect(() => {
        if (!isPending && data?.data) {
            setInteractions(
                data.data.map((i) => ({
                    ...i,
                    createdAt: i.createdAt ? new Date(i.createdAt) : null,
                    updatedAt: i.updatedAt ? new Date(i.updatedAt) : null,
                }))
            );
        }
    }, [data?.data, isPending]);

    const availableSrclangs = useMemo(() => {
        const present = new Set(interactions.map((i) => i.srclang));
        // Keep the spoken language first, then the rest in a stable order.
        return [
            ...(spokenSrclang && present.has(spokenSrclang)
                ? [spokenSrclang]
                : []),
            ...Array.from(present).filter((s) => s !== spokenSrclang),
        ];
    }, [interactions, spokenSrclang]);

    // Fall back to a language that actually has interactions if the default
    // spoken language has none yet.
    const effectiveSrclang =
        selectedSrclang && availableSrclangs.includes(selectedSrclang)
            ? selectedSrclang
            : (availableSrclangs[0] ?? spokenSrclang);

    const interactionsForSrclang = useMemo(
        () => interactions.filter((i) => i.srclang === effectiveSrclang),
        [interactions, effectiveSrclang]
    );

    const { currentStep, setCurrentStep, nextStep, prevStep, moveToStep } =
        useInteractionModalSteps();

    useEffect(() => {
        const step = interactionModalSteps.find((s) => s.id === currentStep);

        if (
            selectedInteraction &&
            step &&
            step.showForTypes &&
            !step.showForTypes.includes(selectedInteraction.type)
        ) {
            nextStep();
        }
    }, [currentStep, nextStep, selectedInteraction, selectedInteraction?.type]);

    const { loading, changes, setChanges, saveChanges } = useSaveInteractions({
        interactions: interactionsForSrclang,
        srclang: effectiveSrclang,
        videoId: video.id,
        setReloadVideoKey,
    });

    const { updateInteraction } = useInteractionEdit({
        video,
        interactions: interactionsForSrclang,
        setInteractions,
        setSelectedInteraction,
        setChanges,
    });

    const {
        createInteraction,
        editInteraction,
        deleteInteraction,
        duplicateInteraction,
        getInteractionTitle,
    } = useInteractionsList({
        video,
        interactions: interactionsForSrclang,
        srclang: effectiveSrclang,
        setInteractions,
        setSelectedInteraction,
        setChanges,
        setInteractionModalOpen,
    });

    const closeModal = () => {
        setInteractionModalOpen(false);
        setSelectedInteraction(null);
        setCurrentStep("time-and-type");
    };

    const value: InteractionsContextType = {
        mode: "videoSheet",
        interactions: interactionsForSrclang,
        selectedSrclang: effectiveSrclang,
        setSelectedSrclang,
        availableSrclangs,
        selectedInteraction,
        setInteractionModalOpen,
        createInteraction,
        updateInteraction,
        deleteInteraction,
        duplicateInteraction,
        getInteractionTitle,
        saveChanges,
        loading,
        changes,
        editInteraction,
        currentStep,
        nextStep,
        prevStep,
        moveToStep,
        closeModal,
        Step1Component: TimeAndTypeStep,
        step1Label: {
            keyName: "interaction-modal.sidebar.time-and-type",
            defaultValue: "זמן וסוג",
        },
    };

    return (
        <InteractionsContext.Provider value={value}>
            <InteractionModal
                open={interactionModalOpen}
                setOpen={setInteractionModalOpen}
            />
            {isPending ? <Loading /> : children}
        </InteractionsContext.Provider>
    );
}

const Loading = () => (
    <div className="py-24 flex w-full justify-center items-center">
        <LoaderCircle className="size-4 animate-spin" />
    </div>
);
